import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminSystemOperations.css";

type Source="ISSUER"|"BADGE_EMAIL"|"EXTERNAL_NOTIFICATION"|"BULK"|"STUDENT_IMPORT"|"ACADEMIC_TRANSITION"|string;
type StatusView="ALL"|"ATTENTION"|"RUNNING"|"COMPLETED";

type SystemJob={
  source:Source;
  jobId:string;
  status:string;
  jobType:string;
  title:string;
  organizationId?:string|null;
  organizationName?:string|null;
  subjectType?:string|null;
  subjectId?:string|null;
  subjectName?:string|null;
  attemptCount?:number|null;
  maxAttempts?:number|null;
  requestedCount?:number|null;
  succeededCount?:number|null;
  failedCount?:number|null;
  nextAttemptAt?:string|null;
  updatedAt?:string|null;
  createdAt:string;
  errorCode?:string|null;
  errorMessage?:string|null;
  correlationId?:string|null;
  retryable:boolean;
  actionKey?:string|null;
  metadata?:Record<string,unknown>;
};

type SystemOps={
  summary:{total:number;running:number;attention:number;failed:number;completed:number};
  infrastructure:{
    pgNetInstalled:boolean;
    pgCronInstalled:boolean;
    badgeWorkerTokenConfigured:boolean;
    notificationWorkerTokenConfigured:boolean;
    resendApiKeyConfigured:boolean;
    emailFromConfigured:boolean;
    emailProviderConfigured:boolean;
    schedulingMode:string;
  };
  sourceCounts:{
    issuer:number;badgeEmail:number;externalNotification:number;bulk:number;studentImport:number;academicTransition:number;
  };
  jobs:SystemJob[];
};

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
}
function sourceLabel(value:string){
  const map:Record<string,string>={
    ISSUER:"Issuer",
    BADGE_EMAIL:"배지 이메일",
    EXTERNAL_NOTIFICATION:"외부 알림",
    BULK:"Bulk Job",
    STUDENT_IMPORT:"학생 Import",
    ACADEMIC_TRANSITION:"학년도 전환"
  };
  return map[value] || value;
}
function statusLabel(value:string){
  const map:Record<string,string>={
    PENDING:"대기",PROCESSING:"처리 중",RETRY:"재시도 대기",SUCCEEDED:"성공",DEAD_LETTER:"Dead Letter",CANCELLED:"취소",
    WAITING_CONTACT:"연락처 대기",PREPARED:"전송 준비",ACCEPTED:"전달 완료",RETRYABLE_FAILURE:"재시도 필요",
    PERMANENT_FAILURE:"영구 실패",CLAIMED:"수령 완료",QUEUED:"큐 대기",SENDING:"전송 중",
    RUNNING:"실행 중",PARTIAL:"부분 실패",FAILED:"실패",APPLIED:"적용 완료"
  };
  return map[value] || value;
}
function isFailure(status:string){return ["DEAD_LETTER","PERMANENT_FAILURE","FAILED"].includes(status);}
function isAttention(status:string){return ["RETRYABLE_FAILURE","PARTIAL","WAITING_CONTACT"].includes(status);}
function isRunning(status:string){return ["PENDING","RETRY","QUEUED","RUNNING","PROCESSING","SENDING","PREPARED"].includes(status);}
function isComplete(status:string){return ["SUCCEEDED","ACCEPTED","CLAIMED","APPLIED"].includes(status);}
function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function AdminSystemOperations({client}:{client:PlatformClient}){
  const [data,setData]=useState<SystemOps|null>(null);
  const [selectedId,setSelectedId]=useState("");
  const [sourceFilter,setSourceFilter]=useState("ALL");
  const [statusView,setStatusView]=useState<StatusView>("ALL");
  const [query,setQuery]=useState("");
  const [reason,setReason]=useState("시스템 운영센터에서 재처리합니다.");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_system_operations",{p_limit:500}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as SystemOps;
      setData(next);
      setSelectedId((current)=>next.jobs.some((item)=>item.source+":"+item.jobId===current)?current:(next.jobs[0]?next.jobs[0].source+":"+next.jobs[0].jobId:""));
    });
    return()=>{active=false;};
  },[client,refresh]);

  const visibleJobs=useMemo(()=>{
    const keyword=query.trim().toLowerCase();
    return (data?.jobs || []).filter((item)=>{
      if(sourceFilter!=="ALL" && item.source!==sourceFilter)return false;
      if(statusView==="ATTENTION" && !(isFailure(item.status)||isAttention(item.status)))return false;
      if(statusView==="RUNNING" && !isRunning(item.status))return false;
      if(statusView==="COMPLETED" && !isComplete(item.status))return false;
      return !keyword || [item.title,item.organizationName || "",item.subjectName || "",item.jobId,item.errorCode || ""]
        .some((value)=>value.toLowerCase().includes(keyword));
    });
  },[data,sourceFilter,statusView,query]);

  const selected=useMemo(
    ()=>data?.jobs.find((item)=>item.source+":"+item.jobId===selectedId) || null,
    [data,selectedId]
  );

  useEffect(()=>{
    if(visibleJobs.length && !visibleJobs.some((item)=>item.source+":"+item.jobId===selectedId)){
      const first=visibleJobs[0];
      setSelectedId(first.source+":"+first.jobId);
    }
  },[visibleJobs,selectedId]);

  async function run(key:string,action:()=>PromiseLike<{error?:{message:string}|null}>,success:string){
    setBusy(key);setError("");setMessage("");
    try{
      const result=await action();
      if(result.error)throw new Error(result.error.message);
      setMessage(success);
      setRefresh((value)=>value+1);
    }catch(err){
      setError(err instanceof Error?err.message:"처리 중 오류가 발생했습니다.");
    }finally{setBusy("");}
  }

  async function triggerIssuer(requestId:string){
    const {data,error}=await client.auth.getSession();
    if(error || !data.session?.access_token)throw new Error("로그인 세션을 확인할 수 없습니다.");
    const response=await fetch("/issuer/process",{
      method:"POST",
      headers:{"content-type":"application/json","authorization":"Bearer "+data.session.access_token},
      body:JSON.stringify({requestId})
    });
    const body=await response.json().catch(()=>({}));
    if(!response.ok)throw new Error(body?.message || "Issuer 처리를 시작하지 못했습니다.");
  }

  async function requeueIssuer(item:SystemJob){
    if(!item.subjectId)return;
    setBusy("issuer-requeue");setError("");setMessage("");
    try{
      const {error}=await client.rpc("admin_requeue_platform_delivery",{
        p_delivery_id:item.jobId,
        p_reason:reason.trim() || "시스템 운영센터 Dead Letter 재처리"
      });
      if(error)throw new Error(error.message);
      await triggerIssuer(item.subjectId);
      setMessage("Dead Letter를 재큐잉하고 Issuer 처리를 다시 시작했습니다.");
      setRefresh((value)=>value+1);
    }catch(err){
      setError(err instanceof Error?err.message:"Issuer 재처리 중 오류가 발생했습니다.");
    }finally{setBusy("");}
  }

  if(!data)return <p className="muted">시스템 Job 상태를 불러오는 중…</p>;

  const infra=data.infrastructure;
  const providerReady=infra.emailProviderConfigured;

  return (
    <section className="system-ops-page">
      <header className="system-ops-hero">
        <div>
          <p className="eyebrow">SYSTEM OPERATIONS & JOB CONTROL CENTER</p>
          <h2>시스템 운영·Job 관제센터</h2>
          <p>Issuer, 이메일 Worker, 외부 알림, Bulk Job, 학생 Import, 학년도 전환 작업을 하나의 Queue 관점에서 추적하고 안전한 재처리만 실행합니다.</p>
        </div>
        <div className={"system-ops-posture "+(data.summary.failed>0?"blocked":data.summary.attention>0?"attention":"ready")}>
          <span>JOB POSTURE</span>
          <strong>{data.summary.failed>0?"실패 작업 있음":data.summary.attention>0?"확인 필요":"정상"}</strong>
          <small>실패 {data.summary.failed} · 확인 {data.summary.attention}</small>
        </div>
      </header>

      <div className="system-ops-kpis">
        <OpsKpi label="전체 Job" value={data.summary.total} />
        <OpsKpi label="실행·대기" value={data.summary.running} />
        <OpsKpi label="확인 필요" value={data.summary.attention} tone={data.summary.attention>0?"warn":"normal"} />
        <OpsKpi label="실패" value={data.summary.failed} tone={data.summary.failed>0?"danger":"normal"} />
        <OpsKpi label="완료" value={data.summary.completed} />
      </div>

      <section className="system-infra-grid">
        <InfraCard label="DB HTTP" state={infra.pgNetInstalled?"PASS":"BLOCKED"} detail={infra.pgNetInstalled?"pg_net 설치됨":"pg_net 미설치"} />
        <InfraCard label="스케줄러" state="INFO" detail={infra.pgCronInstalled?"pg_cron 사용 가능":"Trigger + Worker kick 방식"} />
        <InfraCard label="Badge Worker" state={infra.badgeWorkerTokenConfigured?"PASS":"BLOCKED"} detail={infra.badgeWorkerTokenConfigured?"Worker token 준비":"Worker token 없음"} />
        <InfraCard label="알림 Worker" state={infra.notificationWorkerTokenConfigured?"PASS":"BLOCKED"} detail={infra.notificationWorkerTokenConfigured?"Worker token 준비":"Worker token 없음"} />
        <InfraCard label="Email Provider" state={providerReady?"PASS":"BLOCKED"} detail={providerReady?"Resend + From 구성":"Resend API key / From 확인 필요"} />
      </section>

      <section className="system-worker-panel">
        <div>
          <p className="eyebrow">WORKER CONTROL</p>
          <h3>Worker 수동 실행</h3>
          <p>자동 Trigger가 정상이어도 운영자가 Queue를 즉시 다시 깨울 수 있습니다.</p>
        </div>
        <div className="system-worker-actions">
          <button className="secondary" disabled={busy!==""||!providerReady} onClick={()=>run("badge-worker",()=>client.rpc("admin_system_worker_kick",{p_worker:"BADGE_EMAIL"}),"Badge Email Worker를 호출했습니다.")}>
            {busy==="badge-worker"?"호출 중…":"Badge Email Worker"}
          </button>
          <button className="secondary" disabled={busy!==""||!providerReady} onClick={()=>run("notify-worker",()=>client.rpc("admin_system_worker_kick",{p_worker:"EXTERNAL_NOTIFICATION"}),"External Notification Worker를 호출했습니다.")}>
            {busy==="notify-worker"?"호출 중…":"External Notification Worker"}
          </button>
        </div>
      </section>

      {!providerReady && (
        <div className="system-provider-warning">
          <strong>Email Provider 설정이 완성되지 않았습니다.</strong>
          <span>현재 DEV Vault에서 Resend API key 또는 발신주소가 확인되지 않아 이메일 Worker 수동 실행을 차단했습니다.</span>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="system-ops-toolbar">
        <input value={query} onChange={(event)=>setQuery(event.target.value)} placeholder="Job·기관·학생·오류코드 검색" />
        <select value={sourceFilter} onChange={(event)=>setSourceFilter(event.target.value)}>
          <option value="ALL">전체 소스</option>
          {["ISSUER","BADGE_EMAIL","EXTERNAL_NOTIFICATION","BULK","STUDENT_IMPORT","ACADEMIC_TRANSITION"].map((source)=>
            <option key={source} value={source}>{sourceLabel(source)}</option>
          )}
        </select>
        <div className="system-status-filter">
          {(["ALL","ATTENTION","RUNNING","COMPLETED"] as StatusView[]).map((item)=>
            <button key={item} className={statusView===item?"active":""} onClick={()=>setStatusView(item)}>
              {item==="ALL"?"전체":item==="ATTENTION"?"문제":item==="RUNNING"?"진행중":"완료"}
            </button>
          )}
        </div>
      </div>

      <div className="system-job-layout">
        <aside className="system-job-list">
          <div className="panel-title"><strong>Job Queue</strong><span>{visibleJobs.length}건</span></div>
          {visibleJobs.map((item)=>{
            const key=item.source+":"+item.jobId;
            return (
              <button key={key} className={selectedId===key?"active":""} onClick={()=>setSelectedId(key)}>
                <div>
                  <span className={"system-source "+item.source.toLowerCase()}>{sourceLabel(item.source)}</span>
                  <span className={"system-job-status "+(isFailure(item.status)?"failed":isAttention(item.status)?"attention":isRunning(item.status)?"running":"done")}>{statusLabel(item.status)}</span>
                </div>
                <strong>{item.title}</strong>
                <p>{item.subjectName || item.organizationName || item.subjectType || "-"}</p>
                <small>{formatDate(item.updatedAt || item.createdAt)}</small>
              </button>
            );
          })}
          {!visibleJobs.length && <p className="case-empty">조건에 맞는 Job이 없습니다.</p>}
        </aside>

        <main className="system-job-detail">
          {selected ? (
            <>
              <header className="system-job-detail-head">
                <div>
                  <div className="system-detail-tags">
                    <span className={"system-source "+selected.source.toLowerCase()}>{sourceLabel(selected.source)}</span>
                    <span>{statusLabel(selected.status)}</span>
                  </div>
                  <h2>{selected.title}</h2>
                  <p>{selected.organizationName || "기관 미확인"}{selected.subjectName?" · "+selected.subjectName:""}</p>
                </div>
                {selected.correlationId && <button className="secondary" onClick={()=>navigate("/audit")}>Audit Trace</button>}
              </header>

              <div className="system-job-info-grid">
                <JobInfo label="Job ID" value={selected.jobId} />
                <JobInfo label="Job Type" value={selected.jobType} />
                <JobInfo label="시도" value={selected.attemptCount!=null?(selected.maxAttempts!=null?selected.attemptCount+"/"+selected.maxAttempts:String(selected.attemptCount)):"-"} />
                <JobInfo label="최근 갱신" value={formatDate(selected.updatedAt || selected.createdAt)} />
                <JobInfo label="다음 시도" value={formatDate(selected.nextAttemptAt)} />
                <JobInfo label="Correlation" value={selected.correlationId || "-"} />
              </div>

              {(selected.requestedCount!=null || selected.succeededCount!=null || selected.failedCount!=null) && (
                <div className="system-count-strip">
                  <Count label="요청" value={selected.requestedCount || 0} />
                  <Count label="성공" value={selected.succeededCount || 0} />
                  <Count label="실패" value={selected.failedCount || 0} attention={(selected.failedCount || 0)>0} />
                </div>
              )}

              {(selected.errorCode || selected.errorMessage) && (
                <section className="system-error-card">
                  <p className="eyebrow">LAST ERROR</p>
                  <strong>{selected.errorCode || "ERROR"}</strong>
                  <p>{selected.errorMessage || "상세 오류 메시지 없음"}</p>
                </section>
              )}

              {selected.metadata && Object.keys(selected.metadata).length>0 && (
                <section className="system-metadata-card">
                  <div className="panel-title"><strong>운영 메타데이터</strong><span>{Object.keys(selected.metadata).length} fields</span></div>
                  <div className="system-metadata-grid">
                    {Object.entries(selected.metadata).map(([key,value])=>
                      <div key={key}><span>{key}</span><strong>{typeof value==="object"?JSON.stringify(value):String(value ?? "-")}</strong></div>
                    )}
                  </div>
                </section>
              )}

              <section className="system-action-card">
                <div>
                  <p className="eyebrow">OPERATOR ACTION</p>
                  <h3>허용된 운영 조치</h3>
                </div>

                {selected.source==="ISSUER" && selected.actionKey==="REQUEUE" ? (
                  <>
                    <label>재처리 사유<textarea rows={3} value={reason} onChange={(event)=>setReason(event.target.value)} /></label>
                    <button className="primary" disabled={busy!==""||!reason.trim()} onClick={()=>requeueIssuer(selected)}>
                      {busy==="issuer-requeue"?"재처리 중…":"Dead Letter 재큐잉 + Issuer 실행"}
                    </button>
                  </>
                ) : selected.source==="BADGE_EMAIL" && selected.actionKey==="RETRY" ? (
                  <button className="primary" disabled={busy!==""||!providerReady} onClick={()=>run("badge-retry",()=>client.rpc("admin_retry_badge_email",{p_email_job_id:selected.jobId,p_reason:"시스템 운영센터에서 이메일 재시도"}),"배지 이메일을 재시도 큐에 등록했습니다.")}>
                    {busy==="badge-retry"?"재등록 중…":"배지 이메일 재시도"}
                  </button>
                ) : selected.source==="EXTERNAL_NOTIFICATION" && selected.actionKey==="WORKER_KICK" ? (
                  <button className="primary" disabled={busy!==""||!providerReady} onClick={()=>run("external-kick",()=>client.rpc("admin_system_worker_kick",{p_worker:"EXTERNAL_NOTIFICATION"}),"외부 알림 Worker를 실행했습니다.")}>
                    {busy==="external-kick"?"호출 중…":"외부 알림 Worker 실행"}
                  </button>
                ) : selected.actionKey==="CONTACT_REQUIRED" ? (
                  <div className="system-action-note attention"><strong>학생 연락처 필요</strong><span>학교 Center에서 학생 배지 수신 이메일을 등록하면 Queue가 자동으로 깨어납니다.</span></div>
                ) : selected.actionKey==="CENTER_RETRY" ? (
                  <div className="system-action-note"><strong>학교 권한으로 재시도</strong><span>Bulk Job은 학교 업무문맥을 보존하기 위해 해당 Center의 교사/학교관리자가 재시도합니다.</span></div>
                ) : selected.actionKey==="CENTER_REVIEW" ? (
                  <div className="system-action-note"><strong>학교 Center 확인 필요</strong><span>Import 실패 행을 확인한 뒤 학교에서 수정 파일로 다시 실행합니다.</span></div>
                ) : (
                  <div className="system-action-note good"><strong>직접 조치 없음</strong><span>현재 Job 상태에서는 관리자 수동 재처리가 필요하지 않습니다.</span></div>
                )}
              </section>
            </>
          ):<p className="case-empty">Job을 선택하세요.</p>}
        </main>
      </div>
    </section>
  );
}

function OpsKpi({label,value,tone="normal"}:{label:string;value:number;tone?:"normal"|"warn"|"danger"}){
  return <div className={"system-ops-kpi "+tone}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}
function InfraCard({label,state,detail}:{label:string;state:"PASS"|"BLOCKED"|"INFO";detail:string}){
  return <div className="system-infra-card"><span className={state.toLowerCase()}>{state==="PASS"?"정상":state==="BLOCKED"?"확인":"정보"}</span><div><strong>{label}</strong><small>{detail}</small></div></div>;
}
function JobInfo({label,value}:{label:string;value:string}){
  return <div className="system-job-info"><span>{label}</span><strong>{value}</strong></div>;
}
function Count({label,value,attention=false}:{label:string;value:number;attention?:boolean}){
  return <div className={attention?"system-count attention":"system-count"}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}
